import { useId, useState } from 'react';
import { ChevronDown, History } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type {
  LiveStream,
  LiveStreamDisplayStatus,
  LiveStreamState,
} from '@/types';
import { formatDuration } from '@/utils/dateFormat';
import { LiveStreamStatusBadge } from './LiveStreamStatusBadge';

const legacyDisplayStatus: Record<LiveStreamState, LiveStreamDisplayStatus> = {
  planned: 'planned',
  on_air: 'live',
  ended: 'ended',
  deleted: 'deleted',
};

function formatAttemptDateTime(dateString: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(dateString));
}

function formatAttemptTime(dateString: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(dateString));
}

function getAttemptDuration(attempt: LiveStream): string | null {
  if (!attempt.startedAt || !attempt.endedAt) return null;

  const duration =
    new Date(attempt.endedAt).getTime() - new Date(attempt.startedAt).getTime();
  return formatDuration(Math.max(0, duration));
}

interface LiveStreamHistoryProps {
  attempts: LiveStream[];
  locale: string;
}

export function LiveStreamHistory({
  attempts,
  locale,
}: LiveStreamHistoryProps) {
  const { t } = useTranslation('pages');
  const [isExpanded, setIsExpanded] = useState(false);
  const contentId = useId();

  if (attempts.length < 2) return null;

  return (
    <div className="border-t border-secondary-100">
      <button
        type="button"
        className="group flex w-full items-center justify-between gap-4 px-4 py-4 text-left transition-colors hover:bg-secondary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset sm:px-6"
        aria-expanded={isExpanded}
        aria-controls={contentId}
        onClick={() => setIsExpanded((current) => !current)}
      >
        <span className="flex min-w-0 items-center gap-3">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-600 transition-colors group-hover:bg-secondary-200">
            <History className="h-4 w-4" aria-hidden="true" />
          </span>
          <span className="font-medium text-secondary-900">
            {t('sessions.detail.liveStream.history.title')}
          </span>
        </span>
        <span className="flex flex-shrink-0 items-center gap-3 text-sm text-secondary-500">
          {t('sessions.detail.liveStream.history.attemptCount', {
            count: attempts.length,
          })}
          <ChevronDown
            className={`h-4 w-4 text-secondary-600 transition-transform duration-200 ${
              isExpanded ? 'rotate-180' : ''
            }`}
            aria-hidden="true"
          />
        </span>
      </button>

      {isExpanded && (
        <div
          id={contentId}
          role="region"
          aria-label={t('sessions.detail.liveStream.history.title')}
          className="border-t border-secondary-100 bg-secondary-50/60"
        >
          <ol className="divide-y divide-secondary-100">
            {attempts.map((attempt, index) => {
              const displayStatus =
                attempt.displayStatus ?? legacyDisplayStatus[attempt.state];
              const attemptNumber = attempts.length - index;
              const startedAt = attempt.startedAt ?? attempt.createdAt;
              const duration = getAttemptDuration(attempt);

              return (
                <li key={attempt.id} className="relative px-4 py-4 sm:px-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-secondary-900">
                          {t('sessions.detail.liveStream.history.attempt', {
                            number: attemptNumber,
                          })}
                        </p>
                        {index === 0 && (
                          <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-secondary-500 ring-1 ring-inset ring-secondary-200">
                            {t('sessions.detail.liveStream.history.latest')}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-secondary-500">
                        <time dateTime={startedAt}>
                          {formatAttemptDateTime(startedAt, locale)}
                        </time>
                        {attempt.endedAt && (
                          <>
                            <span aria-hidden="true">·</span>
                            <span>
                              {t('sessions.detail.liveStream.history.endedAt', {
                                time: formatAttemptTime(
                                  attempt.endedAt,
                                  locale
                                ),
                              })}
                            </span>
                          </>
                        )}
                        {duration && (
                          <>
                            <span aria-hidden="true">·</span>
                            <span>
                              {t(
                                'sessions.detail.liveStream.history.duration',
                                { duration }
                              )}
                            </span>
                          </>
                        )}
                      </div>
                      {attempt.description && (
                        <p className="mt-2 text-sm text-secondary-600">
                          {attempt.description}
                        </p>
                      )}
                    </div>
                    <LiveStreamStatusBadge status={displayStatus} />
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
}
